import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import moment from 'moment';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { DeviceFactory } from '../../../device-factory/device-factory';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-daily-monthly-quarterly-battery-usage-charts',
	templateUrl: './daily-monthly-quarterly-battery-usage-charts.component.html',
	styleUrls: ['./daily-monthly-quarterly-battery-usage-charts.component.css']
})
export class DailyMonthlyQuarterlyBatteryUsageChartsComponent {
	siteId = 0;

	@Input() device: any = {};
	@Input() hasVoltageFilter = true;
	@Input() hasCapacityFilter = true;
	@Input() hasVintageFilter = true;
	@Input() hasManufacturerFilter = true;
	@Input() title = this.translate.instant("fleet_reports.monthly_quarterly_battery_usage_charts");
	@Input() currentSite: any = {};
	isDevicePage = false;

	yearList: any[] = [];
	selectedYear: number = new Date().getFullYear();
	siteInstallationDate = null;
	noData = false;

	selectedPeriodType = 'monthly';
	selectedGraphTypes = 'total';
	selectedCharts = 'all';

	chartsData: any = {
		total_hours: {},
		total_ah: {},
		average_hours: {},
		average_ah: {},
	};

	monthlyData: any = {};
	quarterlyData: any = {};

	chartObject = {
		config:  {
			legend: { position: 'top' },
			bar: {
				groupWidth: 30
			},
			tooltip: {isHtml: true},
		},
		columnNames: [this.translate.instant("g.time"), this.translate.instant("g.data")],
		hasData: false
	}

	timeChartConfig: any = {...this.chartObject.config};

	timeChartColumns = [
		this.translate.instant("g.time"),
		this.translate.instant("fleet_reports.inuse_hours"), { role: 'tooltip', type: 'string', p: {html: false}}, // inuse
		this.translate.instant("fleet_reports.charge_hours"), { role: 'tooltip', type: 'string', p: {html: false}}, // charge
		this.translate.instant("fleet_reports.idle_hours"), { role: 'tooltip', type: 'string', p: {html: false}} // idle
	];

	ahChartColumns = [
		this.translate.instant("g.time"),
		this.translate.instant("fleet_reports.inuse_ah"), { role: 'tooltip', type: 'string', p: {html: false}}, // inuse
		this.translate.instant("fleet_reports.charge_ah"), { role: 'tooltip', type: 'string', p: {html: false}}, // charge
	];

	periodTypes = [
		{label: this.translate.instant('g.quarterly'), id: 'quarterly'},
		{label: this.translate.instant('g.monthly'), id: 'monthly'},
		{label: this.translate.instant('g.weekly'), id: 'weekly'},
	];

	graphTypes = [
		{label: this.translate.instant('g.total'), id: 'total'},
		{label: this.translate.instant('g.daily_avg'), id: 'average'},
	];

	chartsList = [
		{id: 'all', label: this.translate.instant('g.all')},
		{id: 'hours', label: this.translate.instant('fleet_reports.time_usage')},
		{id: 'ah', label: this.translate.instant('fleet_reports.ah_usage')},
	];

	batteryVoltagesOptions: any[] = [];
	selectedBatteryVoltage: string = 'all';

	batteryCapacitiesOptions: any[] = [];
	selectedBatteryCapacity: string = 'all';

	vintageOptionsDropdown = this.siteDashboardService.vintageOptionsDropdown;
	selectedBatteryVintage: string[] = ['all'];

	batteryManufacturersOptions: any[] = [];
	selectedBatteryManufacturer: string = 'all';
	allYearsId = 9999;

	get printFilters(): { label: string, value: string }[] {
		const filters: { label: string, value: string }[] = [
			{ label: 'g.year', value: this.commonService.getOptionLabel(this.yearList, this.selectedYear) }
		];

		if (!this.isAllYearsSelected())
			filters.push({ label: 'perf_analytic.period', value: this.commonService.getOptionLabel(this.periodTypes, this.selectedPeriodType) });

		filters.push({ label: 'g.charts', value: this.commonService.getOptionLabel(this.chartsList, this.selectedCharts) });

		if (this.selectedPeriodType !== 'daily' && this.selectedPeriodType !== 'weekly')
			filters.push({ label: 'sim.data', value: this.commonService.getOptionLabel(this.graphTypes, this.selectedGraphTypes) });

		if (this.hasVoltageFilter)
			filters.push({ label: 'fleet_reports.battery_voltage', value: this.commonService.getOptionLabel(this.batteryVoltagesOptions, this.selectedBatteryVoltage) });

		if (this.hasCapacityFilter)
			filters.push({ label: 'devices.battery_capacity', value: this.commonService.getOptionLabel(this.batteryCapacitiesOptions, this.selectedBatteryCapacity) });

		if (this.hasManufacturerFilter)
			filters.push({ label: 'devices.battery_manufacturer', value: this.commonService.getOptionLabel(this.batteryManufacturersOptions, this.selectedBatteryManufacturer) });

		if (this.hasVintageFilter)
			filters.push({ label: 'devices.battery_vintage', value: this.commonService.getOptionLabel(this.vintageOptionsDropdown, this.selectedBatteryVintage) });

		return filters;
	}

	constructor(
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private commonService: CommonService,
		private siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		this.isDevicePage = this.device?.mac_address ? true : false;
		this.siteId = this.route?.parent?.snapshot?.params?.siteId || 0;

		// if (this.isDevicePage)
		// 	this.periodTypes = this.periodTypes.filter((period) => period.id != 'quarterly');

		if (this.device && this.device?.installation_date) {
			this.siteInstallationDate = moment.utc(this.device.installation_date * 1000).startOf('day').toDate();
			this.getChargerUsageChart();
			const {yearList, currentYear} =  this.siteDashboardService.prepareYearsList(this.yearList, this.siteInstallationDate);
			this.yearList = yearList;
			this.yearList.unshift({label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId});
			this.selectedYear = currentYear;
		} else {
			this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
				if (siteInfo && siteInfo.installation_date) {
					this.siteInstallationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
					this.getChargerUsageChart();
					const {yearList, currentYear} =  this.siteDashboardService.prepareYearsList(this.yearList, this.siteInstallationDate);
					this.yearList = yearList;
					this.yearList.unshift({label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId});
					this.selectedYear = currentYear;
				}
			});
		}
	}

	selectYear() {
		if (['daily', 'weekly'].includes(this.selectedPeriodType) && !this.isAllYearsSelected())
			return this.getChargerUsageChart();

		this.checkSelectedYearData();
	}

	isAllYearsSelected() {
		return this.selectedYear === this.allYearsId;
	}

	changePeriodType() {
		if (['daily', 'weekly'].includes(this.selectedPeriodType)) {
			this.selectedGraphTypes = 'total';
			this.noData = false;
			return this.getChargerUsageChart();
		}

		if (this.selectedPeriodType === 'monthly')
			return this.prepareQuarterlyMonthlyData(this.monthlyData);

		return this.prepareQuarterlyMonthlyData(this.quarterlyData);
	}

	getChargerUsageChart() {
		const options: any = {
			is_daily: this.selectedPeriodType === 'daily',
			is_weekly: this.selectedPeriodType === 'weekly',
			year: this.selectedYear,
			site_id: this.siteId,
			sct_devices_types: [DeviceFactory.types.battlink, DeviceFactory.types.lithiumBattlink, DeviceFactory.types.battlinkII],
		};

		if (this.isDevicePage) {
			options.mac_address = this.device.mac_address;
		} else {
			options.batteryVoltage = this.selectedBatteryVoltage;
			options.batteryCapacity = this.selectedBatteryCapacity;
			options.batteryVintage = this.selectedBatteryVintage;
			options.batteryManufacturer = this.selectedBatteryManufacturer;
		}

		this.sitesService.getUsageChart(this.siteId, options).subscribe((res: any) => {
			this.batteryVoltagesOptions = this.commonService.prepareOptions(res.batteryVoltages);
			this.batteryCapacitiesOptions = this.commonService.prepareOptions(res.batteryCapacities);
			this.batteryManufacturersOptions = this.commonService.prepareOptions(res.batteryManufacturers);

			switch (this.selectedPeriodType) {
				case 'daily':
					this.prepareDailyWeeklyData(res.daily);
					break;
				case 'weekly':
					this.prepareDailyWeeklyData(res.weekly);
					break;
				case 'monthly':
				case 'quarterly':
					this.monthlyData = res.monthly;
					this.quarterlyData = res.quarterly;
					let data = this.monthlyData;

					if (this.selectedPeriodType === 'quarterly')
						data = this.quarterlyData;

					this.prepareQuarterlyMonthlyData(data);
					break;
				default:
					break;
			}
		});
	}

	exportDataToCsv() {
		const headersInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device.serial_number
		});

		const csvData = [
			...headersInfo,
		];

		for (let key in this.chartsData) {
			if (this.selectedGraphTypes === 'total' && !key.includes('total'))
				continue;
			if (this.selectedGraphTypes === 'average' && !key.includes('average'))
				continue;
			if (this.selectedCharts === 'hours' && !key.includes('hours'))
				continue;
			if (this.selectedCharts === 'ah' && !key.includes('ah'))
				continue;

			csvData.push([this.translate.instant('fleet_reports.' + key)]);

			if (key.includes('hours')) {
				csvData.push([
					this.translate.instant("g.time"),
					this.translate.instant("fleet_reports.inuse_hours"),
					this.translate.instant("fleet_reports.charge_hours"),
					this.translate.instant("fleet_reports.idle_hours")
				]);

				const dataRows = this.chartsData[key]?.[this.selectedYear] || [];
				for (const item of dataRows) {
					const row = [
						item[0], // time
						item[1], // inuse
						item[3], // charge
						item[5], // idle
					];
					csvData.push(row);
				}
			} else {
				csvData.push([
					this.translate.instant("g.time"),
					this.translate.instant("fleet_reports.inuse_ah"),
					this.translate.instant("fleet_reports.charge_ah")
				]);

				const dataRows = this.chartsData[key]?.[this.selectedYear] || [];
				for (const item of dataRows) {
					const row = [
						item[0], // time
						item[1], // inuse ah
						item[3], // charge ah
					];
					csvData.push(row);

				}
			}
			csvData.push([]);
		}

		const selectedPeriodTypeLabel = this.periodTypes.find((period) => period.id === this.selectedPeriodType)?.label || this.translate.instant('g.monthly');
		new AngularCsv(csvData, `${selectedPeriodTypeLabel}_${this.translate.instant('fleet_reports.battery_usage_charts')}`);
	}

	prepareDailyWeeklyData(data) {
		// Preserve allYearsId data (built by monthly on init) so it survives resetCharts()
		const savedAllYearsHours = this.chartsData.total_hours[this.allYearsId] || [];
		const savedAllYearsAh = this.chartsData.total_ah[this.allYearsId] || [];
		const savedAllYearsAvgHours = this.chartsData.average_hours[this.allYearsId] || [];
		const savedAllYearsAvgAh = this.chartsData.average_ah[this.allYearsId] || [];

		this.resetCharts();

		this.chartsData.total_hours[this.allYearsId] = savedAllYearsHours;
		this.chartsData.total_ah[this.allYearsId] = savedAllYearsAh;
		this.chartsData.average_hours[this.allYearsId] = savedAllYearsAvgHours;
		this.chartsData.average_ah[this.allYearsId] = savedAllYearsAvgAh;

		const yearId = this.selectedYear;
		this.chartsData.total_hours[yearId] = [];
		this.chartsData.total_ah[yearId] = [];

		this.chartObject.columnNames = [this.translate.instant("g.time"), this.translate.instant("g.date")];

		if (!data || !Object.keys(data).length)
			return this.noData = true;

		let dateValue = this.selectedPeriodType == 'daily' ? 'day' : 'week';
		// time is day unix or week (W1)
		for (const time in data) {
			if (data.hasOwnProperty(time) && data[time][dateValue]) {
				const date = data[time][dateValue];
				const totalChargeSeconds = data[time].total_charge_second || 0;
				const totalInuseSeconds = data[time].total_inuse_second || 0;
				const totalIdleSeconds = data[time].total_idle_second || 0;
				const totalChargeAh = data[time].total_charge_as ? +(data[time].total_charge_as / 3600).toFixed(2) : 0;
				const totalInuseAh = data[time].total_inuse_as ? +(data[time].total_inuse_as / 3600).toFixed(2) : 0;

				let chartPeriodLabel = date;
				if (this.selectedPeriodType == 'weekly')
					chartPeriodLabel = `${data[time].week} (${data[time].week_range.start} - ${data[time].week_range.end})`;

				this.chartsData.total_hours[yearId].push([
					date,
					Math.round((totalInuseSeconds || 0) / 3600), this.commonService.formatPopupTime(totalInuseSeconds || 0, chartPeriodLabel), // inuse
					Math.round((totalChargeSeconds || 0) / 3600), this.commonService.formatPopupTime(totalChargeSeconds || 0, chartPeriodLabel), // charge
					Math.round((totalIdleSeconds || 0) / 3600), this.commonService.formatPopupTime(totalIdleSeconds || 0, chartPeriodLabel), // idle
				]);

				this.chartsData.total_ah[yearId].push([
					date, 
					totalInuseAh || 0, this.commonService.formatPopupAh(totalInuseAh || 0, chartPeriodLabel),
					totalChargeAh || 0, this.commonService.formatPopupAh(totalChargeAh || 0, chartPeriodLabel)
				]);
			}
		}
	}

	prepareQuarterlyMonthlyData(data) {
		this.resetCharts();

		if (!data || !Object.keys(data).length)
			return this.noData = true;

		for (let year of this.yearList) {
			const yearId = year.id;
			const totalChargeSeconds =  data?.data_total_battery_charge_duration_seconds?.[yearId] || [];
			const totalInuseSeconds =  data?.data_total_battery_inuse_duration_seconds?.[yearId] || [];
			const totalIdleSeconds =  data?.data_total_battery_idle_duration_seconds?.[yearId] || [];

			const totalChargeAh = data.data_total_battery_charge_as_from_charge_events_only?.[yearId] || [];
			const totalInuseAh = data.data_total_battery_inuse_as_from_inuse_events_only?.[yearId] || [];

			const averageChargeSeconds = data.data_avg_battery_charge_duration_seconds?.[yearId] || [];
			const averageInuseSeconds = data.data_avg_battery_inuse_duration_seconds?.[yearId] || [];
			const averageIdleSeconds = data.data_avg_battery_idle_duration_seconds?.[yearId] || [];

			const averageChargeAh = data.data_avg_battery_charge_as_from_charge_events_only?.[yearId] || [];
			const averageInuseAh = data.data_avg_battery_inuse_as_from_inuse_events_only?.[yearId] || [];

			if (!totalChargeSeconds.length || !totalInuseSeconds.length || !totalIdleSeconds ||
				!totalChargeAh.length || !totalInuseAh.length ||
				!averageChargeSeconds.length || !averageInuseSeconds.length || !averageIdleSeconds.length ||
				!averageChargeAh.length || !averageInuseAh.length
			) {
				continue;
			}

			this.noData = false;
			this.chartsData.total_hours[yearId] = [];
			this.chartsData.total_ah[yearId] = [];
			this.chartsData.average_hours[yearId] = [];
			this.chartsData.average_ah[yearId] = [];

			this.chartsData.total_hours[this.allYearsId] ||= [];
			this.chartsData.total_ah[this.allYearsId] ||= [];
			this.chartsData.average_hours[this.allYearsId] ||= [];
			this.chartsData.average_ah[this.allYearsId] ||= [];

			const yearlyTotalInuseSeconds = {inuse: 0, charge: 0, idle: 0};
			const yearlyTotalInuseAh = {inuse: 0, charge: 0};
			const yearlyAverageInuseSeconds = {inuse: 0, charge: 0, idle: 0};
			const yearlyAverageInuseAh = {inuse: 0, charge: 0};

			let index = 0;
			for(const period of totalInuseSeconds) {
				yearlyTotalInuseSeconds.inuse += period[1] || 0;
				yearlyTotalInuseSeconds.charge += totalChargeSeconds[index][1] || 0;
				yearlyTotalInuseSeconds.idle += totalIdleSeconds[index][1] || 0;

				this.chartsData.total_hours[yearId].push([
					period[0],
					Math.round((period[1] || 0 ) / 3600), this.commonService.formatPopupTime(period[1] || 0, period[0]), // inuse
					Math.round((totalChargeSeconds[index][1] || 0) / 3600), this.commonService.formatPopupTime(totalChargeSeconds[index][1] || 0, period[0]), // charge
					Math.round((totalIdleSeconds[index][1] || 0) / 3600), this.commonService.formatPopupTime(totalIdleSeconds[index][1] || 0, period[0]), // idle
				]);
				index++;
			}

			index = 0;
			for(const period of totalInuseAh) {
				yearlyTotalInuseAh.inuse += period[1] || 0;
				yearlyTotalInuseAh.charge += totalChargeAh[index][1] || 0;

				this.chartsData.total_ah[yearId].push([
					period[0], 
					Math.round(period[1] || 0), this.commonService.formatPopupAh(period[1] || 0, period[0]),
					Math.round(totalChargeAh[index][1] || 0), this.commonService.formatPopupAh(totalChargeAh[index][1] || 0, period[0]),
				]);
				index++;
			}

			index = 0;
			for(const period of averageInuseSeconds) {
				yearlyAverageInuseSeconds.inuse += period[1] || 0;
				yearlyAverageInuseSeconds.charge += averageChargeSeconds[index][1] || 0;
				yearlyAverageInuseSeconds.idle += averageIdleSeconds[index][1] || 0;

				this.chartsData.average_hours[yearId].push([
					period[0],
					Math.round((period[1] || 0) / 3600), this.commonService.formatPopupTime(period[1] || 0, period[0]), // inuse
					Math.round((averageChargeSeconds[index][1] || 0) / 3600), this.commonService.formatPopupTime(averageChargeSeconds[index][1] || 0, period[0]), // charge
					Math.round((averageIdleSeconds[index][1] || 0) / 3600), this.commonService.formatPopupTime(averageIdleSeconds[index][1] || 0, period[0]), // idle
				]);
				index++;
			}

			index = 0;
			for(const period of averageInuseAh) {
				yearlyAverageInuseAh.inuse += period[1] || 0;
				yearlyAverageInuseAh.charge += averageChargeAh[index][1] || 0;

				this.chartsData.average_ah[yearId].push([
					period[0],
					Math.round(period[1] || 0), this.commonService.formatPopupAh(period[1] || 0, period[0]),
					Math.round(averageChargeAh[index][1] || 0), this.commonService.formatPopupAh(averageChargeAh[index][1] || 0, period[0]),
				]);
				index++;
			}

			this.chartsData.total_hours[this.allYearsId].push([
				String(yearId),
				Math.round((yearlyTotalInuseSeconds.inuse || 0 ) / 3600), this.commonService.formatPopupTime(yearlyTotalInuseSeconds.inuse || 0, yearId), // inuse
				Math.round((yearlyTotalInuseSeconds.charge || 0) / 3600), this.commonService.formatPopupTime(yearlyTotalInuseSeconds.charge || 0, yearId), // charge
				Math.round((yearlyTotalInuseSeconds.idle || 0) / 3600), this.commonService.formatPopupTime(yearlyTotalInuseSeconds.idle || 0, yearId), // idle
			]);

			this.chartsData.total_ah[this.allYearsId].push([
				String(yearId),
				Math.round((yearlyTotalInuseAh.inuse || 0)), this.commonService.formatPopupAh(yearlyTotalInuseAh.inuse || 0, yearId),
				Math.round((yearlyTotalInuseAh.charge || 0)), this.commonService.formatPopupAh(yearlyTotalInuseAh.charge || 0, yearId),
			]);

			const monthsDoneInYear = this.getMonthsDoneInYear(yearId);

			this.chartsData.average_hours[this.allYearsId].push([
				String(yearId),
				Math.round((yearlyAverageInuseSeconds.inuse || 0) / 3600 / monthsDoneInYear), this.commonService.formatPopupTime((yearlyAverageInuseSeconds.inuse || 0) / monthsDoneInYear, yearId), // inuse
				Math.round((yearlyAverageInuseSeconds.charge || 0) / 3600 / monthsDoneInYear), this.commonService.formatPopupTime((yearlyAverageInuseSeconds.charge || 0) / monthsDoneInYear, yearId), // charge
				Math.round((yearlyAverageInuseSeconds.idle || 0) / 3600 / monthsDoneInYear), this.commonService.formatPopupTime((yearlyAverageInuseSeconds.idle || 0) / monthsDoneInYear, yearId), // idle
			]);

			this.chartsData.average_ah[this.allYearsId].push([
				String(yearId),
				Math.round((yearlyAverageInuseAh.inuse || 0) / monthsDoneInYear), this.commonService.formatPopupAh((yearlyAverageInuseAh.inuse || 0) / monthsDoneInYear, yearId),
				Math.round((yearlyAverageInuseAh.charge || 0) / monthsDoneInYear), this.commonService.formatPopupAh((yearlyAverageInuseAh.charge || 0) / monthsDoneInYear, yearId),
			]);
		}

		this.chartsData.total_hours[this.allYearsId].sort((a, b) => Number(a[0]) - Number(b[0]));
		this.chartsData.total_ah[this.allYearsId].sort((a, b) => Number(a[0]) - Number(b[0]));
		this.chartsData.average_hours[this.allYearsId].sort((a, b) => Number(a[0]) - Number(b[0]));
		this.chartsData.average_ah[this.allYearsId].sort((a, b) => Number(a[0]) - Number(b[0]));

		this.checkSelectedYearData();
	}

	getMonthsDoneInYear(year: number): number {
		const currentYear = moment().year();

		// Past years => full 12 months
		if (year < currentYear)
			return 12;

		// Current year => current month number
		if (year === currentYear)
			return moment().month() + 1; // month() is 0-based

		// Future years
		return 0;
	}

	resetCharts() {
		this.chartsData = {
			total_hours: {},
			total_ah: {},
			average_hours: {},
			average_ah: {},
		};
	}

	checkSelectedYearData() {
		this.noData = false;
		if (
			!this.chartsData.total_hours[this.selectedYear]?.length &&
			!this.chartsData.total_ah[this.selectedYear]?.length
		) {
			return this.noData = true;
		}
	}

	onCapacityFilter() {
		this.getChargerUsageChart();
	}

	onVoltageFilter() {
		this.getChargerUsageChart();
	}

	batteryVintageSelectionChanged() {
		if (!this.selectedBatteryVintage.length)
			this.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (this.selectedBatteryVintage.length > 1 && this.selectedBatteryVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.selectedBatteryVintage[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.selectedBatteryVintage = this.selectedBatteryVintage.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}

		this.getChargerUsageChart();
	}

}
